import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle } from 'lucide-react';

import {
  $validationResult,
  $hasValidationErrors,
} from '../../_state/flowValidation.state';

export const ValidationPanel: React.FC = () => {
  const result = useAtomValue($validationResult);
  const hasErrors = useAtomValue($hasValidationErrors);

  if (!result || result.issues.length === 0) return null;

  const errorCount = result.issues.filter((i) => i.severity === 'error').length;
  const warningCount = result.issues.filter(
    (i) => i.severity === 'warning',
  ).length;

  const formatSummary = () => {
    const errorLabel = errorCount === 1 ? 'error' : 'errors';
    const warningLabel = warningCount === 1 ? 'warning' : 'warnings';

    if (errorCount > 0 && warningCount > 0) {
      return `${errorCount} ${errorLabel}, ${warningCount} ${warningLabel}`;
    }
    if (errorCount > 0) {
      return `${errorCount} ${errorLabel} found`;
    }
    return `${warningCount} ${warningLabel} found`;
  };

  return (
    <div className="@ValidationPanel absolute bottom-4 left-1/2 z-10 max-w-2xl min-w-80 -translate-x-1/2">
      <div
        className={cn(
          'rounded-lg border p-4 shadow-lg',
          hasErrors
            ? 'border-failure bg-failure/10'
            : 'border-warning bg-warning/10',
        )}
      >
        <div className="mb-3 flex items-center gap-2">
          {hasErrors ? (
            <AlertCircle className="size-5 text-failure" />
          ) : (
            <AlertTriangle className="size-5 text-warning" />
          )}
          <span className="font-semibold text-neutral">{formatSummary()}</span>
        </div>

        <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
          {result.issues.map((issue, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 rounded-md bg-white/50 px-3 py-2"
            >
              {issue.severity === 'error' ? (
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-failure" />
              ) : (
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
              )}
              <div className="flex-1">
                <p className="font-medium text-neutral">{issue.message}</p>
                {issue.field && (
                  <p className="mt-0.5 text-xs text-neutral/60">
                    {issue.field}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
